import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/_atoms/select";

import { DatatableRowData } from "../datatable-types";
import { buildColumnDisplayValueComparator } from "../datatable-utils/build-column-display-value-comparator";
import { useDatatableContext } from "../use-datatable-context";

import { useColumnDisplayValue } from "./use-column-display-value";

const NO_SECTION_OPTION = "__NO_SECTION__";

export function DatatableDisplayOptionsSectioningSelect<TData extends DatatableRowData>() {
  const { t } = useTranslation("common");
  const { table, getConfigurationForColumn, setState, state, lock } = useDatatableContext<TData>();
  const { sectionColumnId } = state;

  const getDisplayValueForColumn = useColumnDisplayValue<TData>();

  const sectioningColumnList = useMemo(() => {
    return table
      .getAllColumns()
      .filter(
        (column) => getConfigurationForColumn(column.id)?.enableSectioning === true && lock.canOfferColumn(column),
      )
      .sort(buildColumnDisplayValueComparator(getDisplayValueForColumn));
  }, [table, getDisplayValueForColumn, getConfigurationForColumn, lock]);

  const handleValueChange = (columnId: string) => {
    if (columnId === NO_SECTION_OPTION) {
      setState({
        state: {
          sectionColumnId: null,
          subSectionColumnId: null,
        },
      });
    } else {
      setState({
        state: {
          sectionColumnId: columnId,
        },
      });
    }
  };

  if (sectioningColumnList.length === 0) {
    return <></>;
  }

  return (
    <div className="flex w-full flex-row items-center gap-1">
      <Select value={sectionColumnId ?? ""} onValueChange={handleValueChange}>
        <SelectTrigger className="h-6 px-2 text-xs ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-focus">
          <div className="flex flex-row items-center justify-between text-xs">
            {!sectionColumnId && t("view.clickToGroup")}
            {sectionColumnId && <>{getDisplayValueForColumn(sectionColumnId)}</>}
          </div>
        </SelectTrigger>
        <SelectContent className="z-10000 p-0">
          {sectioningColumnList.map((column) => {
            return (
              <SelectItem
                key={column.id}
                value={column.id}
                className="flex h-6 flex-row items-center justify-between text-xs"
              >
                {getDisplayValueForColumn(column.id)}
              </SelectItem>
            );
          })}
          <SelectItem value={NO_SECTION_OPTION} className="flex h-6 flex-row items-center justify-between text-xs">
            {t("view.noGrouping")}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
